/**
 * Routes legacy IDocumentAction objects to their corresponding apply* mutation functions.
 *
 * This is a simplified replacement for the old handler chain (handlers.ts + ActionHandler arrays).
 * It's used by dispatchBatch for backward compatibility with callers that still produce
 * IDocumentAction objects (UserCodeRuntimeActionMapper, SceneManager, test utilities).
 *
 * New code should use direct store methods or batchMutate instead.
 */

import type {IDocumentAction} from "@buildwithflux/core";
import {
    addElements,
    applyActionRecordsToDoc,
    deleteSubjects,
    handleUpdateElementsLabel,
    revertActionRecords as revertActionRecordsFromCore,
    updateDescription,
} from "@buildwithflux/core";
import {toggleGlobalPcbRule} from "@buildwithflux/frontend-e2e-shared";
import type {IDocumentData} from "@buildwithflux/models";

import {FluxLogger} from "modules/storage_engine/connectors/LogConnector";

import {
    applyActionRecords,
    mergeDocumentData,
    selectObjects,
    setAssetUserData,
    setAssets,
    setControlUserData,
    setElementUserData,
    setNodeLabelMulti,
    setNodeRulesMulti,
    setNodeUserData,
    setRuleSetRules,
    setRuleSetSelector,
    setRulesetUserData,
    removeAssets,
    updateCommentThreadsPositionAnchor,
    updateElementProperties,
    updateElementsLabel,
    updateElementsSchematicPosition,
    updateRouteProperties,
    updateRoutesLabel,
    updateWiring,
    updatePlotsPosition,
    lockObjects,
    flipElements,
    updatePropertyVisibility,
    setCode,
    setControls,
    updateSimulationTimeStep,
    updateSimulationTimeStepUnit,
    setSimulationControlsValue,
    setDocumentConfigs,
    createCommentThread,
    deleteCommentThread,
    incrementCommentThreadCommentCount,
    pinCommentThread,
    resolveCommentThread,
    unresolveCommentThread,
    setRole,
    removeRole,
    setProjectInvite,
    removeProjectInvite,
    setDocumentAsPrivate,
    setEnterpriseMemberPermissionType,
    setOrganizationMemberPermissionType,
    createProjectDoc,
    deleteProjectDoc,
    updateProjectDoc,
    updateName,
    mergeSubDocumentData,
    deleteSubDocumentData,
    addSubjectsFromClipboard,
    addSubjectsPropertiesFromClipboard,
    revertActionRecords,
    convertSubjectsToNewPart,
    createLocalPart,
    setBelongsToPartUid,
    applyCopilotActions,
    setDocumentDataFromSnapshot,
    replaceElementPartVersionDataCache,
    setPartVersionDataCache,
    setHeadPartVersionDataCache,
} from "./actionCreators/documentActions";
import {addNodes, removeNodes, setNodeLabel, addRouteSegment} from "./actionCreators/pcbLayoutNodes";
import {
    setGlobalPcbRuleSet,
    duplicateGlobalPcbRuleSet,
    removeGlobalPcbRuleSet,
} from "./actionCreators/pcbRuleSetActions";
import {applyRemoveAssets, applySetAssets} from "./actions/assetActions";
import {applyAddSubjectsFromClipboard, applyAddSubjectsPropertiesFromClipboard} from "./actions/clipboardActions";
import {
    applyCreateCommentThread,
    applyDeleteCommentThread,
    applyIncrementCommentThreadCommentCount,
    applyPinCommentThread,
    applyResolveCommentThread,
    applyUnresolveCommentThread,
    applyUpdateCommentThreadsPositionAnchor,
} from "./actions/commentThreadActions";
import {applyDeleteSubjects} from "./actions/deleteSubjectsActions";
import {applySetDocumentDataFromSnapshot} from "./actions/documentDataActions";
import {
    applyAddElements,
    applyFlipElements,
    applySetPartVersionDataCache,
    applyUpdateElementProperties,
    applyUpdateElementsSchematicPosition,
} from "./actions/elementActions";
import {
    applyApplyCopilotActions,
    applyConvertSubjectsToNewPart,
    applyCreateLocalPart,
    applyDeleteSubDocumentData,
    applyMergeSubDocumentData,
    applyReplaceElementPartVersionDataCache,
    applySetAssetUserData,
    applySetBelongsToPartUid,
    applySetControlUserData,
    applySetElementUserData,
    applySetRulesetUserData,
    applyUpdatePlotsPosition,
    applyUpdatePropertyVisibility,
    applyUpdateRouteProperties,
    applyUpdateRoutesLabel,
    applyUpdateWiring,
} from "./actions/miscActions";
import {
    applyAddNodes,
    applyRemoveNodes,
    applySetNodeLabel,
    applySetNodeLabelMulti,
    applySetNodeRulesMulti,
    applySetNodeUserData,
    applySetRuleSetRules,
    applySetRuleSetSelector,
} from "./actions/pcbNodeActions";
import {
    applyDuplicateGlobalPcbRuleSet,
    applyRemoveGlobalPcbRuleSet,
    applySetGlobalPcbRuleSet,
    applySetNodeRules,
    applyToggleGlobalPcbRule,
} from "./actions/pcbRulesActions";
import {
    applyRemoveProjectInvite,
    applyRemoveRole,
    applySetDocumentAsPrivate,
    applySetEnterpriseMemberPermissionType,
    applySetOrganizationMemberPermissionType,
    applySetProjectInvite,
    applySetRole,
} from "./actions/permissionActions";
import {applyAddRouteSegment} from "./actions/routeActions";
import {applyLockObjects} from "./actions/selectionActions";
import {
    applySetCode,
    applySetControls,
    applySetDocumentConfigs,
    applySetSimulationControlsValue,
    applyUpdateSimulationTimeStep,
    applyUpdateSimulationTimeStepUnit,
} from "./actions/simulationActions";

/**
 * Routes a legacy IDocumentAction to its corresponding apply* mutation function.
 *
 * Returns true if the action was handled, false otherwise.
 */
export function routeActionToDraft(
    draft: IDocumentData,
    action: IDocumentAction,
    originalDocument?: IDocumentData,
): boolean {
    if (selectObjects.match(action)) {
        draft.selectedObjectUids = action.payload.selectedObjectUids;
        return true;
    }
    if (updateElementsSchematicPosition.match(action)) {
        applyUpdateElementsSchematicPosition(draft, action.payload.elements, action.payload.vertexUpdates);
        return true;
    }
    if (updateWiring.match(action)) {
        applyUpdateWiring(draft, action.payload.vertexUpdates, action.payload.selectedObjectUids);
        return true;
    }
    if (updateCommentThreadsPositionAnchor.match(action)) {
        applyUpdateCommentThreadsPositionAnchor(draft, action.payload.comment_threads);
        return true;
    }
    if (deleteSubjects.match(action)) {
        applyDeleteSubjects(
            draft,
            action.payload.subjectUids,
            action.payload.vertexUpdates ?? [],
            action.payload.deselectUids,
        );
        return true;
    }
    if (setNodeLabelMulti.match(action)) {
        applySetNodeLabelMulti(draft, action.payload.nodeUidLabelMap);
        return true;
    }
    if (setNodeRulesMulti.match(action)) {
        applySetNodeRulesMulti(draft, action.payload.nodeUidRulesMap);
        return true;
    }
    if (setNodeUserData.match(action)) {
        applySetNodeUserData(draft, action.payload.nodeUid, action.payload.userData);
        return true;
    }
    if (setRulesetUserData.match(action)) {
        applySetRulesetUserData(draft, action.payload.rulesetUid, action.payload.userData);
        return true;
    }
    if (setElementUserData.match(action)) {
        applySetElementUserData(draft, action.payload.elementUid, action.payload.userData);
        return true;
    }
    if (setAssetUserData.match(action)) {
        applySetAssetUserData(draft, action.payload.assetUid, action.payload.userData);
        return true;
    }
    if (setControlUserData.match(action)) {
        applySetControlUserData(draft, action.payload.controlUid, action.payload.userData);
        return true;
    }
    if (setRuleSetRules.match(action)) {
        applySetRuleSetRules(draft, action.payload.ruleSetUid, action.payload.rules);
        return true;
    }
    if (setRuleSetSelector.match(action)) {
        applySetRuleSetSelector(draft, action.payload.ruleSetUid, action.payload.selector);
        return true;
    }
    if (updateElementsLabel.match(action)) {
        handleUpdateElementsLabel(draft, action.payload.elements, FluxLogger);
        return true;
    }
    if (updateRoutesLabel.match(action)) {
        applyUpdateRoutesLabel(draft, action.payload.routes);
        return true;
    }
    if (updateElementProperties.match(action)) {
        applyUpdateElementProperties(draft, action.payload.elementUid, action.payload.properties);
        return true;
    }
    if (updateRouteProperties.match(action)) {
        applyUpdateRouteProperties(draft, action.payload.routeUid, action.payload.properties);
        return true;
    }
    if (updatePlotsPosition.match(action)) {
        applyUpdatePlotsPosition(draft, action.payload.elementId, action.payload.position, action.payload.attached);
        return true;
    }
    if (lockObjects.match(action)) {
        applyLockObjects(draft, action.payload.objectUids, action.payload.locked, action.payload.mode);
        return true;
    }
    if (flipElements.match(action)) {
        applyFlipElements(draft, action.payload.elements, action.payload.routeVertexUpdates);
        return true;
    }
    if (updatePropertyVisibility.match(action)) {
        applyUpdatePropertyVisibility(
            draft,
            action.payload.elementIds,
            action.payload.propertyId,
            action.payload.visibility,
        );
        return true;
    }
    if (setCode.match(action)) {
        applySetCode(draft, action.payload.code, action.payload.hasErrors);
        return true;
    }
    if (setControls.match(action)) {
        applySetControls(draft, action.payload.controls);
        return true;
    }
    if (updateSimulationTimeStep.match(action)) {
        applyUpdateSimulationTimeStep(draft, action.payload.value);
        return true;
    }
    if (updateSimulationTimeStepUnit.match(action)) {
        applyUpdateSimulationTimeStepUnit(draft, action.payload.unit);
        return true;
    }
    if (setSimulationControlsValue.match(action)) {
        applySetSimulationControlsValue(
            draft,
            action.payload.elementId,
            action.payload.controlId,
            action.payload.value,
            action.currentUser?.uid ?? "",
        );
        return true;
    }
    if (setDocumentConfigs.match(action)) {
        applySetDocumentConfigs(draft, action.payload.configs, action.currentUser?.uid ?? "");
        return true;
    }
    if (setAssets.match(action)) {
        applySetAssets(draft, action.payload.setAssets);
        return true;
    }
    if (removeAssets.match(action)) {
        applyRemoveAssets(draft, action.payload.removeAssetUids);
        return true;
    }
    if (createCommentThread.match(action)) {
        applyCreateCommentThread(draft, action.payload.commentThread);
        return true;
    }
    if (deleteCommentThread.match(action)) {
        applyDeleteCommentThread(draft, action.payload.commentThreadUid);
        return true;
    }
    if (incrementCommentThreadCommentCount.match(action)) {
        applyIncrementCommentThreadCommentCount(draft, action.payload.commentThreadUid);
        return true;
    }
    if (pinCommentThread.match(action)) {
        applyPinCommentThread(draft, action.payload.commentThreadUid, action.payload.pinned);
        return true;
    }
    if (resolveCommentThread.match(action)) {
        applyResolveCommentThread(draft, action.payload.commentThreadUid);
        return true;
    }
    if (unresolveCommentThread.match(action)) {
        applyUnresolveCommentThread(draft, action.payload.commentThreadUid);
        return true;
    }
    if (setRole.match(action)) {
        applySetRole(draft, action.payload.role);
        return true;
    }
    if (removeRole.match(action)) {
        applyRemoveRole(draft, action.payload.roleUid);
        return true;
    }
    if (setProjectInvite.match(action)) {
        applySetProjectInvite(draft, action.payload.invite);
        return true;
    }
    if (removeProjectInvite.match(action)) {
        applyRemoveProjectInvite(draft, action.payload.invite);
        return true;
    }
    if (setDocumentAsPrivate.match(action)) {
        applySetDocumentAsPrivate(draft);
        return true;
    }
    if (setEnterpriseMemberPermissionType.match(action)) {
        applySetEnterpriseMemberPermissionType(draft, action.payload.permissionType);
        return true;
    }
    if (setOrganizationMemberPermissionType.match(action)) {
        applySetOrganizationMemberPermissionType(draft, action.payload.permissionType);
        return true;
    }
    if (createProjectDoc.match(action) || deleteProjectDoc.match(action) || updateProjectDoc.match(action)) {
        return true;
    }
    if (updateName.match(action)) {
        draft.name = action.payload.newDocumentName;
        draft.slug = action.payload.newDocumentSlug;
        return true;
    }
    if (setDocumentDataFromSnapshot.match(action)) {
        applySetDocumentDataFromSnapshot(draft, action.payload.documentData);
        return true;
    }
    if (applyActionRecords.match(action)) {
        applyActionRecordsToDoc(draft, action.payload.actionRecords);
        return true;
    }
    if (revertActionRecords.match(action)) {
        revertActionRecordsFromCore(draft, action.payload.actionRecords);
        return true;
    }
    if (mergeDocumentData.match(action)) {
        Object.assign(draft, action.payload.partialDocumentData);
        return true;
    }
    if (replaceElementPartVersionDataCache.match(action)) {
        applyReplaceElementPartVersionDataCache(draft, action.payload.elements);
        return true;
    }
    if (setPartVersionDataCache.match(action)) {
        const elementsToChange = Object.values(draft.elements).filter(
            (el) =>
                el.part_uid === action.payload.partUid &&
                el.part_version_data_cache.version !== action.payload.partVersionData.version,
        );
        if (elementsToChange.length > 0) {
            applySetPartVersionDataCache(draft, elementsToChange, action.payload.partVersionData);
        }
        return true;
    }
    if (setHeadPartVersionDataCache.match(action)) {
        const elementsToChange = Object.values(draft.elements).filter(
            (el) =>
                el.part_uid === action.payload.partUid &&
                el.part_version_data_cache.version !== action.payload.partVersionData.version,
        );
        if (elementsToChange.length > 0) {
            applySetPartVersionDataCache(draft, elementsToChange, action.payload.partVersionData);
        }
        return true;
    }
    if (mergeSubDocumentData.match(action)) {
        applyMergeSubDocumentData(draft, action.payload.subDocumentDatas);
        return true;
    }
    if (deleteSubDocumentData.match(action)) {
        applyDeleteSubDocumentData(draft, action.payload.elementUids);
        return true;
    }
    if (addSubjectsFromClipboard.match(action)) {
        applyAddSubjectsFromClipboard(draft, action.payload.elements, action.payload.routeVertices);
        return true;
    }
    if (addSubjectsPropertiesFromClipboard.match(action)) {
        applyAddSubjectsPropertiesFromClipboard(
            draft,
            action.payload.targetElementUids,
            action.payload.targetRouteUids,
            action.payload.properties,
        );
        return true;
    }
    if (convertSubjectsToNewPart.match(action)) {
        applyConvertSubjectsToNewPart(
            draft,
            action.payload.elements,
            action.payload.newElement,
            action.payload.vertexUpdates,
        );
        return true;
    }
    if (createLocalPart.match(action)) {
        applyCreateLocalPart(draft, action.payload.element, action.payload.newElement);
        return true;
    }
    if (setBelongsToPartUid.match(action)) {
        applySetBelongsToPartUid(draft, action.payload.partUid);
        return true;
    }
    if (applyCopilotActions.match(action)) {
        applyApplyCopilotActions(
            draft,
            action.payload.actions,
            action.payload.portalPart,
            action.payload.groundPart,
            action.payload.userUid,
            action.payload.resultCallback,
        );
        return true;
    }

    if (addElements.match(action)) {
        applyAddElements(draft, action.payload);
        return true;
    }
    if (addNodes.match(action)) {
        applyAddNodes(draft, action.payload.nodes, originalDocument ?? draft);
        return true;
    }
    if (removeNodes.match(action)) {
        applyRemoveNodes(
            draft,
            action.payload.removeNodeUids,
            action.payload.toggleBakedNodes,
            action.payload.updatedNodesRulesSetsMap,
        );
        return true;
    }
    if (setNodeLabel.match(action)) {
        applySetNodeLabel(draft, action.payload.nodeUid, action.payload.label);
        return true;
    }
    if (toggleGlobalPcbRule.match(action)) {
        applyToggleGlobalPcbRule(draft, action.payload.ruleId, action.payload.state, action.payload.subRuleId);
        return true;
    }
    if (duplicateGlobalPcbRuleSet.match(action)) {
        applyDuplicateGlobalPcbRuleSet(draft, action.payload.ruleUids);
        return true;
    }
    if (removeGlobalPcbRuleSet.match(action)) {
        applyRemoveGlobalPcbRuleSet(draft, action.payload.pcbLayoutRuleUids);
        return true;
    }
    if (addRouteSegment.match(action)) {
        applyAddRouteSegment(
            draft,
            action.payload.addedNodes,
            action.payload.deletedNodes,
            action.payload.updatedNodePositions,
        );
        return true;
    }
    if (updateDescription.match(action)) {
        draft.description = action.payload.description;
        return true;
    }

    // PCB rules actions (used by RotatePcbNodeMenuItem batch)
    if (action.type === "setNodeRules") {
        applySetNodeRules(draft, action.payload.updatedNodes);
        return true;
    }
    if (setGlobalPcbRuleSet.match(action)) {
        applySetGlobalPcbRuleSet(draft, action.payload.setPcbLayoutRules, action.payload.selectedGlobalPcbLayoutRule);
        return true;
    }

    // eslint-disable-next-line no-console
    console.warn("[legacyActionRouter] Unhandled action type:", action.type);
    return false;
}
